import { CustomerModelType } from '@teinor/erp/types/company/customer'
import SingleCategoryDashboard from '../../../../baseComponents/SingleCategoryDashboard'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import CustomerContactPersonsDashboard from './Components/CustomerContactPersonsDashboard'
import CustomerProfileDataDashboard from './Components/CustomerProfileDataDashboard'
import CustomerRecentTasksDashboard from './Components/CustomerRecentTasksDashboard'
import { WidgetDictionary } from '../../../../baseComponents/Dashboard/useDashboardWidgets/types'
import CustomerProjectsDashboard from './Components/CustomerProjectsDashboard'
import CustomerSellOrdersDashboard from './Components/CustomerSellOrdersDashboard'
import CustomerBagOfHoursDashboard from './Components/CustomerBagOfHoursDashboard'
import CustomerBanksDashboard from './Components/CustomerBanksDashboard'
import CustomerAddressDashboard from './Components/CustomerAddressesDashboard'
import CustomerCampaignsDashboard from './Components/CustomerCampaignsDashboard'
import CustomerNotesDashboard from './Components/CustomerNotesDashboard'
import CustomerTaskCalendarDashboard from './Components/CustomerTaskCalendarDashboard'
import CustomerActivityDashboard from './Components/CustomerActivityDashboard'
import { getEmployeeParams } from '../../../../utils/reducers/getters'
import CustomerOpportunitiesDashboard from './Components/CustomerOpportunitiesDashboard'

interface CustomerDashboardProps extends CustomerDashboardLogicProps { }

const CustomerDashboard: React.FC<CustomerDashboardProps> = ({ basicData, getData }) => {
    const { t } = useTwinTranslation()
    const { allWidgets } = useCustomerDashboardLogic({ basicData, getData })
    const defaultStyle = getEmployeeParams()?.['appStyle'] || 'new1'
    const defaultWidgets = {
        default: [{ "cols": 1, "rows": 2, "name": "customer_info_data" }, { "cols": 1, "rows": 2, "name": "customer_contact_persons" }, { "cols": 1, "rows": 2, "name": "customer_recent_tasks" }, { "cols": 2, "rows": 3, "name": "customer_projects" }, { "cols": 1, "rows": 3, "name": "customer_notes" }, { "cols": 2, "rows": 4, "name": "customer_quote_orders" }, { "cols": 1, "rows": 4, "name": "customer_activity" }, { "cols": 2, "rows": 3, "name": "customer_campaigns" }, { "cols": 1, "rows": 3, "name": "customer_bags_of_hours" }],
        new1: [{ "cols": 1, "rows": 2, "name": "customer_info_data" }, { "cols": 1, "rows": 2, "name": "customer_contact_persons" }, { "cols": 2, "rows": 2, "name": "customer_recent_tasks" }, { "cols": 2, "rows": 3, "name": "customer_projects" }, { "cols": 2, "rows": 3, "name": "customer_notes" }, { "cols": 2, "rows": 4, "name": "customer_quote_orders" }, { "cols": 2, "rows": 4, "name": "customer_activity" }, { "cols": 2, "rows": 3, "name": "customer_campaigns" }, { "cols": 2, "rows": 3, "name": "customer_bags_of_hours" }],
    }
    
    const props = {
        name: 'dashboard_customer_profile',
        title: t('customer', 'Cliente'),
        defaultWidgets: JSON.stringify(defaultWidgets[defaultStyle]), 
        allWidgets
    }
    return (<SingleCategoryDashboard  {...props} forceFamily='customer' />)
}

interface CustomerDashboardLogicProps {
    basicData: CustomerModelType | null
    getData: () => Promise<void>
}

const useCustomerDashboardLogic = ({ basicData, getData }: CustomerDashboardLogicProps ) => {
    const { t } = useTwinTranslation()
    const DSBWidgetTitlesTranslatedParsed = JSON.stringify({
        customer_customer_data: t('customerData', 'Datos del cliente'),
        customer_contact_persons: t('people', 'Personas'),
        customer_recent_tasks: t('recentTasks', 'Tareas recientes'),
        customer_task_calendar: t('calendarTask', 'Calendario de tareas'),
        customer_projects: t('projects', 'Proyectos'),
        customer_opportunities: t('opportunities', 'Oportunidades'),
        customer_quote_orders: t('quotes', 'Presupuestos'),
        customer_activity: t('activity', 'Actividad'),
        customer_notes: t('notes', 'Notas'),
        customer_bags_of_hours: t('bagsOfHours', 'Bolsas de horas'),
        customer_banks: t('banks', 'Bancos'),
        customer_address: t('addresses', 'Direcciones'),
        customer_campaigns: t('campaigns', 'Campañas'),
    })

    const DSBWidgetTitlesTranslated = JSON.parse(DSBWidgetTitlesTranslatedParsed)
    const allWidgets: WidgetDictionary = {
        'customer_info_data': {
            component: CustomerProfileDataDashboard,
            cols: 1,
            rows: 2,
            permission: 'customer.profile.main',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_customer_data,
                allRowData: basicData
            },
            family: 'customer'
        },
        'customer_contact_persons': {
            component: CustomerContactPersonsDashboard,
            cols: 1,
            rows: 2,
            permission: 'customer.profile.contactPerson',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_contact_persons,
                allRowData: basicData
            },
            family: 'customer'
        },
        'customer_recent_tasks': {
            component: CustomerRecentTasksDashboard,
            cols: 1,
            rows: 2,
            permission: 'customer.profile.tasks',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_recent_tasks,
                allRowData: basicData
            },
            family: 'customer'
        },
        'customer_projects': {
            component: CustomerProjectsDashboard,
            cols: 2,
            rows: 3,
            permission: 'customer.profile.projects',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_projects,
                allRowData: basicData
            },
            family: 'customer'
        },
        'customer_opportunities': {
            component: CustomerOpportunitiesDashboard,
            cols: 2,
            rows: 3,
            permission: 'customer.profile.projects',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_opportunities,
                allRowData: basicData
            },
            family: 'customer'
        },
        'customer_quote_orders': {
            component: CustomerSellOrdersDashboard,
            cols: 2,
            rows: 3,
            permission: 'customer.profile.quotes',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_quote_orders,
                allRowData: basicData
            },
            family: 'customer'
        },
        'customer_activity': {
            component: CustomerActivityDashboard,
            cols: 2,
            rows: 3,
            permission: 'customer.profile.notes',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_activity,
                allRowData: basicData
            },
            family: 'customer'
        },
        'customer_notes': {
            component: CustomerNotesDashboard,
            cols: 2,
            rows: 3,
            permission: 'customer.profile.notes',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_notes,
                allRowData: basicData,
                getData
            },
            family: 'customer'
        },
        'customer_bags_of_hours': {
            component: CustomerBagOfHoursDashboard,
            cols: 2,
            rows: 3,
            permission: 'customer.profile.bagOfHours',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_bags_of_hours,
                allRowData: basicData
            },
            family: 'customer'
        },
        'customer_banks': {
            component: CustomerBanksDashboard,
            cols: 2,
            rows: 3,
            permission: 'customer.profile.bank',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_banks,
                allRowData: basicData
            },
            family: 'customer'
        },
        'customer_address': {
            component: CustomerAddressDashboard,
            cols: 2,
            rows: 3,
            permission: 'customer.profile.address',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_address,
                allRowData: basicData
            },
            family: 'customer'
        },
        'customer_campaigns': {
            component: CustomerCampaignsDashboard,
            cols: 2,
            rows: 3,
            permission: 'customer.profile.campaign',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_campaigns,
                allRowData: basicData
            },
            family: 'customer'
        },
        'customer_task_calendar': {
            component: CustomerTaskCalendarDashboard,
            cols: 2,
            rows: 3,
            permission: 'customer.profile.tasks',
            extraProps: {
                title: DSBWidgetTitlesTranslated.customer_task_calendar,
                allRowData: basicData,
                getData
            },
            family: 'customer'
        }
    }

    return { allWidgets }
}

export default CustomerDashboard